if (!customElements.get("store-visit-video-modal")) {
  customElements.define(
    "store-visit-video-modal",
    class StoreVisitVideoModal extends HTMLElement {
      constructor() {
        super();

        this.openedBy = null;
        this._listenersAttached = false;
        this._isMoving = false;
        this._waitingForBody = false;

        this._onClick = this._onClick.bind(this);
        this._onKeyDown = this._onKeyDown.bind(this);
        this._onSectionUnload = this._onSectionUnload.bind(this);
        this._onDocumentReady = this._onDocumentReady.bind(this);
      }

      connectedCallback() {
        if (
          document.body &&
          this.parentNode !== document.body &&
          this._moveToBody()
        ) {
          return;
        }

        if (!document.body) this._waitForBody();
        this._addEventListeners();
      }

      disconnectedCallback() {
        // Moving a connected custom element invokes both lifecycle callbacks.
        // Keep its listeners intact during that internal reparent operation.
        if (this._isMoving) return;

        this._stopWaitingForBody();
        this._removeEventListeners();
        this.hide();
      }

      show(opener) {
        if (this.openedBy && this.openedBy !== opener) {
          this._setOpenerExpanded(this.openedBy, false);
        }

        this.openedBy = opener || null;

        window.pauseAllMedia?.();

        if (document.body) {
          document.body.classList.add("overflow-hidden");
        }
        this.setAttribute("open", "");
        this._setOpenerExpanded(this.openedBy, true);

        const video = this.querySelector("video");
        this._pauseAndResetVideo(video);

        const closeButton = this.querySelector(
          "[data-store-visit-video-close]",
        );
        if (closeButton && typeof closeButton.focus === "function") {
          closeButton.focus();
        }

        this._playVideoWithSound(video);
      }

      hide() {
        const opener = this.openedBy;

        this._pauseAndResetVideo(this.querySelector("video"));
        this.removeAttribute("open");
        if (document.body) {
          document.body.classList.remove("overflow-hidden");
        }

        this._setOpenerExpanded(opener, false);
        this.openedBy = null;

        if (opener && typeof opener.focus === "function") {
          opener.focus();
        }
      }

      _moveToBody() {
        if (
          !document.body ||
          !this.isConnected ||
          this.parentNode === document.body
        ) {
          return false;
        }

        this._isMoving = true;
        try {
          document.body.appendChild(this);
        } finally {
          this._isMoving = false;
        }

        return true;
      }

      _waitForBody() {
        if (this._waitingForBody) return;

        this._waitingForBody = true;
        document.addEventListener("DOMContentLoaded", this._onDocumentReady, {
          once: true,
        });
      }

      _stopWaitingForBody() {
        if (!this._waitingForBody) return;

        document.removeEventListener(
          "DOMContentLoaded",
          this._onDocumentReady,
        );
        this._waitingForBody = false;
      }

      _onDocumentReady() {
        this._waitingForBody = false;
        this._moveToBody();
      }

      _addEventListeners() {
        if (this._listenersAttached) return;

        this.addEventListener("click", this._onClick);
        this.addEventListener("keydown", this._onKeyDown);
        document.addEventListener(
          "shopify:section:unload",
          this._onSectionUnload,
        );
        this._listenersAttached = true;
      }

      _removeEventListeners() {
        if (!this._listenersAttached) return;

        this.removeEventListener("click", this._onClick);
        this.removeEventListener("keydown", this._onKeyDown);
        document.removeEventListener(
          "shopify:section:unload",
          this._onSectionUnload,
        );
        this._listenersAttached = false;
      }

      _onClick(event) {
        const target = event.target;
        const closeControl =
          target && typeof target.closest === "function"
            ? target.closest("[data-store-visit-video-close]")
            : null;

        if (closeControl && this.contains(closeControl)) {
          event.preventDefault();
          this.hide();
          return;
        }

        if (target === this) this.hide();
      }

      _onKeyDown(event) {
        if (!this.hasAttribute("open")) return;

        const isEscape =
          event.key === "Escape" ||
          event.code === "Escape" ||
          event.keyCode === 27;
        if (isEscape) {
          event.preventDefault();
          event.stopPropagation();
          this.hide();
          return;
        }

        const isTab =
          event.key === "Tab" || event.code === "Tab" || event.keyCode === 9;
        if (!isTab) return;

        const focusableElements = this._getFocusableElements();
        if (!focusableElements.length) {
          event.preventDefault();
          return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;
        const focusIsOutside = !this.contains(activeElement);

        if (event.shiftKey && (activeElement === first || focusIsOutside)) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (activeElement === last || focusIsOutside)
        ) {
          event.preventDefault();
          first.focus();
        }
      }

      _getFocusableElements() {
        const selector = [
          "button",
          "a[href]",
          "area[href]",
          "input:not([type='hidden'])",
          "select",
          "textarea",
          "video[controls]",
          "[tabindex]",
        ].join(", ");

        return Array.from(this.querySelectorAll(selector)).filter((element) => {
          if (
            element.hasAttribute("disabled") ||
            element.getAttribute("aria-hidden") === "true" ||
            element.closest("[hidden], [inert], fieldset[disabled]")
          ) {
            return false;
          }

          return element.tabIndex >= 0;
        });
      }

      _setOpenerExpanded(opener, expanded) {
        if (opener && typeof opener.setAttribute === "function") {
          opener.setAttribute("aria-expanded", expanded ? "true" : "false");
        }
      }

      _pauseAndResetVideo(video) {
        if (!video) return;

        try {
          video.pause();
        } catch (error) {
          // A media element can be unavailable while its Shopify section is
          // being torn down. The remaining modal cleanup should still run.
        }

        try {
          video.currentTime = 0;
        } catch (error) {
          // Some browsers reject seeking before media metadata is available.
        }
      }

      _playVideoWithSound(video) {
        if (!video) return;

        video.muted = false;
        video.defaultMuted = false;
        video.removeAttribute("muted");

        try {
          const playRequest = video.play();
          if (playRequest && typeof playRequest.catch === "function") {
            // Browser autoplay policies may still reject playback; the native
            // controls remain available for an explicit retry.
            playRequest.catch(() => {});
          }
        } catch (error) {
          // Keep the modal usable if playback is unavailable.
        }
      }

      _onSectionUnload(event) {
        if (!this._matchesSectionUnload(event)) return;

        this.hide();
        this.remove();
      }

      _matchesSectionUnload(event) {
        const sectionId = this.getAttribute("data-section-id");
        if (!sectionId) return false;

        const detail = event.detail || {};
        const detailSectionId =
          detail.sectionId !== undefined
            ? detail.sectionId
            : detail.section_id;

        if (detailSectionId !== undefined && detailSectionId !== null) {
          return String(detailSectionId) === sectionId;
        }

        const target = event.target;
        if (!target || target.nodeType !== 1) return false;

        if (
          target.getAttribute("data-section-id") === sectionId ||
          target.id === `shopify-section-${sectionId}`
        ) {
          return true;
        }

        return Array.from(
          target.querySelectorAll("[data-section-id]"),
        ).some((element) => element.getAttribute("data-section-id") === sectionId);
      }
    },
  );
}
